import React, { ReactElement, useEffect } from 'react';
import PlaceIcon from '@material-ui/icons/Place';
import { makeStyles, Tooltip } from '@material-ui/core';
import Switch from 'components/UI/Switch';
import { Location } from 'types/common';

interface SelectLocationStripeProps {
  toggleDrawer: (close: boolean) => void;
  selectedLocation: Location | null;
  locationToogle?: boolean;
  setLocationToggle: (locationToggle: boolean) => void;
  stickyFilters: boolean;
  isLocationTooltipOpen: boolean;
  setLocationTooltipOpen: (isLocationTooltipOpen: boolean) => void;
  isMobile: boolean;
}

const TOOLTIP_AUTO_HIDE_DURATION = 5000;

const tooltipStyles = makeStyles({
  tooltip: {
    margin: 0,
    maxWidth: '360px',
    fontSize: '13px',
    // padding: '5px 15px',
    backgroundColor: '#fff',
    color: '#000',
    borderRadius: '5px 5px 5px 0px',
    boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.15)',
    '@media (max-width: 768px)': {
      borderRadius: '5px 5px 5px 5px',
    },
    '@media (max-width: 510px)': {
      fontSize: '12px',
      width: '300px',
    },
    '@media (max-width: 386px)': {
      fontSize: '10px',
      maxWidth: '200px',
    },
  },
  arrow: {
    color: 'white',
    left: '-4px !important',
    bottom: '-6px !important',
    height: '20px',

    '@media (max-width: 768px)': {
      top: '2px !important',
      left: '10px !important',
      height: '8px',
    },
    '@media (max-width: 560px)': {
      top: '2px !important',
      left: '10px !important',
      height: '8px',
    },
    '@media (max-width: 386px)': {
      top: '0px !important',
      left: '10px !important',
      height: '8px',
    },
  },
  tooltipPlacementTop: {
    top: '-5px',
  },
});

const SelectLocationStripe = ({
  toggleDrawer,
  selectedLocation,
  locationToogle,
  setLocationToggle,
  stickyFilters,
  isLocationTooltipOpen,
  setLocationTooltipOpen,
  isMobile,
}: SelectLocationStripeProps): ReactElement => {
  const toolTipClass = tooltipStyles();

  const handleToggleDrawer = (): void => {
    toggleDrawer(true);
  };

  useEffect(() => {
    if (isMobile && isLocationTooltipOpen) {
      const tooltipTimeout = setTimeout(() => {
        setLocationTooltipOpen(false);
      }, TOOLTIP_AUTO_HIDE_DURATION);

      return () => {
        clearTimeout(tooltipTimeout);
      };
    }
  }, [isLocationTooltipOpen, isMobile, setLocationTooltipOpen]);

  const renderLocationButton = (): ReactElement => {
    const locationName = selectedLocation?.name || 'Select your Location';

    return (
      <h3 className="font-heading text-white text-lg cursor-pointer">
        <button
          className="outline-inherit"
          style={{ outline: 'inherit' }}
          onClick={handleToggleDrawer}
        >
          {locationName}
        </button>
      </h3>
    );
  };

  const tooltipContent = (
    <Tooltip
      PopperProps={{ disablePortal: isMobile, style: { zIndex: 60 } }}
      title={`Please click on the "${
        selectedLocation?.name ? selectedLocation.name : 'Select your Location'
      }" option`}
      arrow={true}
      classes={toolTipClass}
      placement={isMobile ? 'bottom-start' : 'top-start'}
      open={isMobile ? isLocationTooltipOpen : undefined}
    >
      {renderLocationButton()}
    </Tooltip>
  );

  return (
    <div
      className={`bg-locationBar p-2  ${stickyFilters ? 'md:px-8 lg:px-16 xl:px-28 mt-0' : 'mt-4'}`}
    >
      <div className={`flex items-center ${stickyFilters ? 'xl:pl-12' : 'px-2'}`}>
        <div
          className={`text-highlight-actual cursor-pointer ${selectedLocation?.name ? 'mb-2' : ''}`}
        >
          {!selectedLocation?.name ? (
            <PlaceIcon color="inherit" style={{ fontSize: 45 }} onClick={handleToggleDrawer} />
          ) : (
            <Switch
              checked={locationToogle}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLocationToggle(e.target.checked)
              }
            />
          )}
        </div>

        <div>
          {isLocationTooltipOpen ? (
            isMobile ? (
              tooltipContent
            ) : (
              <div>{tooltipContent}</div>
            )
          ) : (
            renderLocationButton()
          )}

          <p className="text-white text-sm mt-1 cursor-pointer">
            <button
              onClick={handleToggleDrawer}
              className="outline-inherit text-left"
              style={{ outline: 'inherit' }}
            >
              See what frames are available for try-on in your local office today
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SelectLocationStripe;
